'use client'

import { motion } from 'framer-motion'
import { Handshake, Bot, ArrowRight } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import Link from 'next/link'

const crossSells = [
  {
    name: 'Brand Partnerships',
    description: 'Supply products to the Marketplace and thousands of retail partners. One listing, many sales channels.',
    href: '/brand-partnerships',
    icon: Handshake,
    theme: 'primary' as const,
  },
  {
    name: 'Buddy AI Assistant',
    description: 'Add AI sales, support, and conversion to your own store so more visitors convert after they discover you.',
    href: '/buddy-ai-assistant',
    icon: Bot,
    theme: 'ai' as const,
  },
]

const themeStyles = {
  primary: {
    card: 'bg-panel border-line hover:border-brand-green-line',
    icon: 'bg-brand-green-soft text-brand-green',
    title: 'text-ink',
    body: 'text-muted',
    link: 'text-brand-green',
  },
  ai: {
    card: 'bg-panel border-line hover:border-green-500/30',
    icon: 'bg-raised text-brand-green',
    title: 'text-ink',
    body: 'text-muted',
    link: 'text-brand-green',
  },
}

export function MarketplaceCrossSellSection() {
  return (
    <Section background="gray">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <p className="text-micro-uppercase text-brand-blue mb-3">GROW FURTHER</p>
          <h2 className="text-display-1 text-ink mb-3">
            Pair the Marketplace with the Full Buddify Stack
          </h2>
          <p className="text-body-lg text-muted max-w-2xl mx-auto">
            The Marketplace works best when your catalog and storefront are powered by the same growth engine.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {crossSells.map((item, index) => {
            const theme = themeStyles[item.theme]
            const Icon = item.icon

            return (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  href={item.href}
                  className={`group block h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${theme.card}`}
                >
                  <div className={`inline-flex p-3 rounded-xl mb-4 ${theme.icon}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`text-h4 mb-2 ${theme.title}`}>{item.name}</h3>
                  <p className={`text-body mb-5 ${theme.body}`}>{item.description}</p>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${theme.link}`}>
                    Learn more
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </Container>
    </Section>
  )
}
